import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Star, Moon, Sun, Heart, Coins } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API } from '../utils/helpers';

export default function CreditsPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [packages, setPackages] = useState({});
  const [loading, setLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);

  useEffect(() => {
    fetch(`${API}/credits/packages`).then(res => res.json()).then(setPackages);
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (sessionId && user) checkPaymentStatus(sessionId);
  }, [user]);

  const checkPaymentStatus = async (sessionId) => {
    setCheckingPayment(true);
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await fetch(`${API}/credits/status/${sessionId}`, { credentials: 'include' });
        const data = await res.json();
        if (data.payment_status === 'paid') {
          toast.success(`Pago exitoso! +${data.credits_added} creditos`);
          await refreshUser(); setCheckingPayment(false);
          window.history.replaceState({}, '', '/credits'); return;
        }
        if (attempts < 5 && data.status !== 'expired') { attempts++; setTimeout(poll, 2000); }
        else setCheckingPayment(false);
      } catch (e) { setCheckingPayment(false); }
    };
    poll();
  };

  const buyPackage = async (packageId) => {
    if (!user) { toast.error('Inicia sesion para comprar creditos'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/credits/checkout`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ package_id: packageId, origin_url: window.location.origin })
      });
      if (!res.ok) { toast.error((await res.json()).detail); return; }
      window.location.href = (await res.json()).url;
    } catch (e) { toast.error('Error al procesar el pago'); }
    finally { setLoading(false); }
  };

  const packageList = [
    { key: 'starter', popular: false },
    { key: 'mystic', popular: true },
    { key: 'celestial', popular: false },
  ];

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl sm:text-4xl font-bold gold-text text-center mb-4" data-testid="credits-title">Creditos Misticos</h1>
        <p className="text-[var(--text-muted)] text-center mb-8">Desbloquea lecturas premium y cartas natales completas</p>
        {checkingPayment && <div className="glass-card p-6 mb-8 text-center"><div className="spinner mx-auto mb-4" /><p>Verificando tu pago...</p></div>}
        {user && (
          <div className="glass-card p-6 mb-8 text-center">
            <p className="text-[var(--text-muted)] mb-2">Tu balance actual</p>
            <div className="flex items-center justify-center gap-2">
              <Coins className="w-8 h-8 text-purple-400" />
              <span className="text-4xl font-bold gold-text">{user.credits}</span>
              <span className="text-[var(--text-muted)]">creditos</span>
            </div>
          </div>
        )}
        <div className="grid sm:grid-cols-3 gap-6 mb-12">
          {packageList.map(({ key, popular }) => {
            const pkg = packages[key]; if (!pkg) return null;
            return (
              <motion.div key={key} whileHover={{ scale: 1.02 }} className={`glass-card p-6 text-center ${popular ? 'border-gold glow-gold' : ''}`} style={popular ? { borderColor: 'var(--gold)' } : {}}>
                {popular && <span className="text-xs uppercase tracking-wider mb-2 block text-purple-400">Mas Popular</span>}
                <h3 className="text-xl font-bold mb-2">{pkg.name}</h3>
                <p className="text-4xl font-bold gold-text mb-1">{pkg.credits}</p>
                <p className="text-[var(--text-muted)] text-sm mb-4">creditos</p>
                <p className="text-2xl font-bold mb-6">${pkg.price}</p>
                <button onClick={() => buyPackage(key)} disabled={loading || !user} className={popular ? 'btn-gold w-full' : 'btn-ghost w-full'} data-testid={`buy-${key}`}>
                  {loading ? <span className="spinner w-5 h-5 mx-auto" /> : 'Comprar'}
                </button>
              </motion.div>
            );
          })}
        </div>
        <div className="glass-card p-8">
          <h2 className="text-xl font-bold gold-text mb-6 text-center">Que puedes desbloquear?</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { name: 'Cruz Celta (10 cartas)', credits: 5, icon: Star, section: 'Tarot' },
              { name: 'Herradura (7 cartas)', credits: 3, icon: Moon, section: 'Tarot' },
              { name: 'Tirada del Amor (5 cartas)', credits: 3, icon: Heart, section: 'Tarot' },
              { name: 'La Estrella (7 cartas)', credits: 5, icon: Star, section: 'Tarot' },
              { name: 'Arbol de la Vida (10 cartas)', credits: 7, icon: Star, section: 'Tarot' },
              { name: 'Carta Natal Completa', credits: 8, icon: Sun, section: 'Astrologia' },
              { name: 'I Ching - Mutaciones', credits: 3, icon: Moon, section: 'I Ching' },
              { name: 'I Ching - Consulta Profunda', credits: 5, icon: Sun, section: 'I Ching' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-4 p-4 rounded-lg bg-white/5">
                <item.icon className="w-8 h-8 text-purple-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm">{item.name}</p>
                  <p className="text-[10px] text-[var(--text-muted)]">{item.section}</p>
                </div>
                <span className="credit-badge shrink-0"><Coins className="w-3 h-3" /> {item.credits}</span>
              </div>
            ))}
          </div>
        </div>
        {!user && <p className="text-center text-[var(--text-muted)] mt-8"><Link to="/" className="text-purple-400 underline">Inicia sesion</Link> para comprar creditos</p>}
      </div>
    </div>
  );
}
